import type { MouseEvent } from 'react';
import type { WidgetInstanceProps } from '@source-components-react/types';

/** The only key `r-button` reads out of the host's per-instance props. */
export const BUTTON_CLICK_HANDLER_PROP = 'onClick';

export type ButtonClickHandler = (
  event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>
) => void;

/**
 * The host's click handler for this instance, or `undefined` when there is
 * nothing to attach.
 *
 * Two reasons to answer `undefined` even when the host supplied something:
 *
 * - **Disabled wins.** A disabled plain `<button>` is inert natively, but a
 *   disabled *linked* button renders `<a role="link" aria-disabled="true">`,
 *   which has no native inertness at all — a handler left on it would fire on
 *   every click and make "disabled" a lie.
 * - **The bag is untyped.** It crosses a package boundary as
 *   `{ [key: string]: any }`, so a host that passes a string here should get a
 *   button that does nothing rather than a React crash on the customer's page.
 */
export const resolveButtonClickHandler = ({
  widgetInstanceProps,
  disabled,
}: {
  widgetInstanceProps?: WidgetInstanceProps;
  disabled: boolean;
}): ButtonClickHandler | undefined => {
  if (disabled) {
    return undefined;
  }
  const handler = widgetInstanceProps?.[BUTTON_CLICK_HANDLER_PROP];
  return typeof handler === 'function' ? (handler as ButtonClickHandler) : undefined;
};
